import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '~/components/ui/dialog'
import { useQueryClientHelper } from '~/hooks/use-query-client'
import { cn } from '~/libs/classnames'
import { useAppStore } from '~/stores/app'
import { DialogId } from '~/types/common'
import { QueryKey } from '~/types/query-key'
import type { SchoolImportResult as SchoolImportResultData } from '~/types/school'
import { SchoolCsvImportForm } from './school-csv-import-form'
import { SchoolImportResult } from './school-import-result'

/**
 * Hosts the CSV import form + result panel in a dialog (moved off the
 * standalone `/schools` page so the route can be a full list/management
 * screen). Owns the import result so it can decide the dialog's size: a
 * clean import keeps the compact default width/height, but a result with
 * row errors widens the dialog AND caps its height (`max-h-[85vh]`), with
 * only the body (not the header) scrolling — so the close button and title
 * stay put no matter how many error rows there are.
 *
 * The form is only mounted while the dialog is open, so its internal file
 * input state always starts fresh on next open; `result` lives here and is
 * explicitly cleared on close for the same reason.
 */
export function SchoolImportDialog() {
  const { t } = useTranslation()
  const { invalidateQueries } = useQueryClientHelper()
  const openDialogIds = useAppStore((state) => state.openDialogIds)
  const closeDialog = useAppStore((state) => state.closeDialog)

  const isOpen = openDialogIds.includes(DialogId.IMPORT_SCHOOLS)
  const [result, setResult] = useState<SchoolImportResultData | null>(null)
  const hasErrors = (result?.errors.length ?? 0) > 0

  const handleImported = (importResult: SchoolImportResultData) => {
    setResult(importResult)
    invalidateQueries(QueryKey.SCHOOLS)
  }

  const handleOpenChange = (open: boolean) => {
    if (open) return
    closeDialog(DialogId.IMPORT_SCHOOLS)
    setResult(null)
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className={cn(hasErrors && 'flex max-h-[85vh] max-w-3xl flex-col')}>
        <DialogHeader>
          <DialogTitle>{t('schools.importTitle')}</DialogTitle>
        </DialogHeader>
        {isOpen && (
          <div className={cn('space-y-4', hasErrors && 'min-h-0 flex-1 overflow-y-auto')}>
            <SchoolCsvImportForm onImported={handleImported} />
            {result && <SchoolImportResult result={result} />}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
